import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { HandCoins, Info } from "@phosphor-icons/react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// ZEN-770 piece 2 (owner, 15 Oct 2026): what is owed, per beneficiary and per
// currency — never added across beneficiaries or currencies — with her whole
// total at the latest rate underneath in grey. The server works every figure out.
// Layout and both sentences approved by the owner, 19 Oct 2026, with "latest
// rate" in place of "today's rate": the rate's date can be an earlier day.
export const LATEST_RATE_NOTE =
  "Latest rate, for reference only. What is owed is worked out at each repayment’s own date, and this figure is not used for any tax figure.";

export function latestRateLine(inAll, rateDate) {
  if (!inAll?.length || !rateDate) return null;
  const figures = inAll.map((t) => `or ${money(t.amount, t.currency)}`).join(", ");
  return `${figures}${inAll.length > 1 ? ", in all" : ""} at the latest rate (${fmtDate(rateDate)})`;
}

export function useAdvanceTotals() {
  // Under ["advances"], so every refresh of the advances refreshes these too.
  return useQuery({
    queryKey: ["advances", "totals"],
    queryFn: async () => (await api.get("/advance-totals")).data,
  });
}

// One beneficiary's figures: a line per currency, then the grey latest-rate
// line. Shared by this banner and the Overview's Cash Advances box and panel. (ZEN-779)
export function OwedFigures({ row, className = "tnum text-sm font-bold text-[#92400E]" }) {
  const grey = latestRateLine(row.in_all, row.rate_date);
  return (<>
    {row.owed.map((o) => (
      <div key={o.currency} className={className}>{money(o.amount, o.currency)} outstanding</div>
    ))}
    {grey && (
      <div className="flex items-center gap-1 text-xs italic text-[#6b7280]" data-testid={`advances-latest-rate-${row.student_id}`}>
        <span className="tabular-nums">{grey}</span>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info size={13} weight="bold" className="shrink-0 cursor-help text-[#9aa1a9]" />
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs">{LATEST_RATE_NOTE}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    )}
  </>);
}

export function AdvanceTotalsBanner({ totals, nameOf }) {
  if (!totals?.beneficiaries?.length) return null;
  const rows = [...totals.beneficiaries].sort((a, b) => nameOf(a.student_id).localeCompare(nameOf(b.student_id)));
  const n = totals.advance_count;
  return (
    <div className="mb-4 rounded-lg border border-[#D97706]/40 bg-[#FEF3C7]/40 px-4 py-3" data-testid="advances-outstanding-banner">
      <div className="flex items-start gap-3">
        <HandCoins size={20} weight="bold" className="mt-0.5 shrink-0 text-[#D97706]" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start gap-x-8 gap-y-3">
            {rows.map((r) => {
              return (
                <div key={r.student_id} data-testid={`advances-owed-${r.student_id}`}>
                  <div className="text-xs text-[#92400E]">{nameOf(r.student_id)}</div>
                  <OwedFigures row={r} />
                </div>
              );
            })}
      </div>
      <div className="mt-2 text-xs text-[#92400E]">{n} advance{n === 1 ? "" : "s"} awaiting repayment</div>
        </div>
      </div>
    </div>
  );
}
